import type { PaymentMethodResponse } from '@kimuno/api-sdk/lib/types/responses/Billing';
import type { LbMigration, UserResponseData } from '@kimuno/api-sdk/lib/types/responses/User';

import type { Subject } from '../constants';

export interface Address {
	[key: string]: string;
	areaCode: string;
	city: string;
	country: string;
	street: string;
	state: string;
}

export interface InvoiceAddress {
	[key: string]: string;
	gender: string;
	firstName: string;
	lastName: string;
	areaCode: string;
	city: string;
	country: string;
	street: string;
	state: string;
	institution: string;
}

// same as types from PaymentMethodResponse, but more strict, directly change at api-sdk can break existing code
export const UserPaymentMethodType = {
	SEPA: 'sepa',
	PAYPAL: 'paypal',
	CREDIT_CARD: 'creditCard',
	BANK_TRANSFER: 'bankTransfer',
	INVOICE: 'invoice',
} as const;

export type UserPaymentMethodType =
	(typeof UserPaymentMethodType)[keyof typeof UserPaymentMethodType];

export interface ManageableSchool {
	id: string;
	name: string;
	stripeCustomerId?: string;
}

export type WithdrawalRequestStatus = 'requested';

export interface User {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
	classes: number[];
	schoolType?: string;
	school?: {
		placeId: string;
		name: string;
		formattedAddress: string;
		state: string;
	};
	subjects: UserResponseData['subjects'];
	subjectsFlattened: Subject[];
	isAdmin: boolean;
	isCoreMember: boolean;
	isMUMember: boolean;
	isSubscribedToNewsletter: boolean;
	isFree: boolean; // User themselves is not paying for their subscription, e.g. school, library
	isReadOnlyLicence: boolean;
	isTrainee: boolean | undefined;
	isTrial: boolean | undefined;
	isInTrial: boolean | undefined;
	isParent: boolean;
	hasDocumentLevelAccess: boolean;
	isPremium: boolean;
	isBasisUser: boolean;
	isBibliothekUser: boolean;
	isCourseUser: boolean;
	isKombiUser: boolean;
	isAiOnlyUser: boolean;
	isWalkIn: boolean;
	isKeycloakSSO?: boolean;
	isLuxSSO?: boolean;
	// isSchoolUser aka "School membership user" / "B2B user"
	// Has an active premium subscription with a school licence
	isSchoolUser: boolean;
	isMigratedSchoolUser: boolean; // School user that has been migrated to schulen subdomain
	isLibraryUser: boolean;
	isStripeUser: boolean; // User's billing is managed by Stripe (has a mapped Stripe customer)
	hideCollectionAction: boolean;
	hideDownloadAction: boolean;
	hidePinAction: boolean;
	choseToStayBasis?: boolean;
	redirectTo?: string;
	downloads: {
		counter: number;
		max: number;
	};
	address: Address;
	invoiceAddress: InvoiceAddress;
	gender: string;
	birthday: string | number;
	paymentMethod?: PaymentMethodResponse;
	licenceId: number;
	onboardingAttempts: number;
	createdAt: number;
	allowAutomaticCancellation?: boolean;
	pauseExperimentStatus?: string;
	withdrawalRequestStatus?: WithdrawalRequestStatus;
	withdrawalRequestedAt?: number;
	signUpMethod?: string;
	type?: number;
	teacherType?: string;
	didReachCoursesLimit?: boolean;
	canUpgrade?: boolean;
	isSchoolAdmin?: boolean;
	teacherRoles?: string[];
	federalState?: string;
	schoolStage?: string; // deprecated, use schoolStages instead
	schoolStages?: string[];
	groupId?: string;
	manageableSchools?: ManageableSchool[];
	lbMigration?: LbMigration;
	isMukiBlocked?: boolean;
}
